import { useState } from "react";
import { useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listProjectSuggestions,
  analyzeProjectSuggestions,
  acceptProjectSuggestion,
  dismissProjectSuggestion,
  type ProjectSuggestion,
  type ProjectSuggestionTask,
} from "@/lib/project-suggestions-api";
import {
  getListProjectsQueryKey,
  getListTasksQueryKey,
  getGetTodayQueryKey,
} from "@workspace/api-client-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles,
  Loader2,
  Check,
  X,
  Wand2,
  ChevronDown,
  ChevronUp,
  ExternalLink,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const PROJECT_SUGGESTIONS_QUERY_KEY = ["project-suggestions"] as const;

export default function SuggestedProjectsSection() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { data, isLoading } = useQuery({
    queryKey: PROJECT_SUGGESTIONS_QUERY_KEY,
    queryFn: () => listProjectSuggestions(),
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });

  const suggestions = data?.suggestions ?? [];
  const [hasInteracted, setHasInteracted] = useState(false);
  // When analysis is queued (background), poll until suggestions appear.
  const [polling, setPolling] = useState(false);
  const [pollStartedAt, setPollStartedAt] = useState<number | null>(null);

  // Poll every 4 s while analysis is in flight; stop after 5 min or on results.
  useQuery({
    queryKey: [...PROJECT_SUGGESTIONS_QUERY_KEY, "poll"],
    queryFn: async () => {
      const r = await listProjectSuggestions();
      if (r.suggestions.length > 0) {
        qc.setQueryData(PROJECT_SUGGESTIONS_QUERY_KEY, r);
        setPolling(false);
      } else if (pollStartedAt && Date.now() - pollStartedAt > 5 * 60 * 1000) {
        setPolling(false);
      }
      return r;
    },
    enabled: polling,
    refetchInterval: polling ? 4000 : false,
    refetchOnWindowFocus: false,
  });

  const analyzeMutation = useMutation({
    mutationFn: () => analyzeProjectSuggestions(),
    onMutate: () => {
      setHasInteracted(true);
    },
    onSuccess: (r) => {
      if (r.reason === "queued" || r.reason === "already_running") {
        // Analysis is running in background — start polling.
        setPolling(true);
        setPollStartedAt(Date.now());
        return;
      }
      qc.invalidateQueries({ queryKey: PROJECT_SUGGESTIONS_QUERY_KEY });
      if (r.created > 0) {
        toast({
          title: `${r.created} ${r.created === 1 ? "regroupement" : "regroupements"} proposé${r.created === 1 ? "" : "s"}`,
        });
      } else if (r.reason === "ai_cap_reached") {
        toast({
          title: "Quota IA quotidien atteint",
          description: "Réessayez demain ou augmentez votre quota.",
          variant: "destructive",
        });
      } else if (r.reason === "not_enough_tasks") {
        toast({
          title: "Toutes les tâches sont déjà dans un projet",
          description:
            "Vos projets existent peut-être déjà — changez le filtre sur « Tous » pour les voir.",
        });
      } else if (r.reason === "ai_failed") {
        toast({
          title: "L'analyse IA a échoué",
          description: "Réessayez dans quelques instants.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Aucun regroupement proposé",
          description: "L'IA n'a pas trouvé de tâches à grouper.",
        });
      }
    },
    onError: (err: Error) => {
      toast({
        title: "Impossible de lancer l'analyse",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  return (
    <section className="mb-8" data-testid="section-project-suggestions">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" />
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Projets suggérés
          </h2>
          {suggestions.length > 0 && (
            <Badge variant="secondary" className="text-xs font-mono">
              {suggestions.length}
            </Badge>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          className="rounded-full"
          onClick={() => { setHasInteracted(true); analyzeMutation.mutate(); }}
          disabled={analyzeMutation.isPending || polling}
          data-testid="button-analyze-tasks"
        >
          {(analyzeMutation.isPending || polling) ? (
            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
          ) : (
            <Wand2 className="w-3.5 h-3.5 mr-1.5" />
          )}
          {polling ? "Analyse en cours…" : "Analyser mes tâches"}
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-32 w-full rounded-2xl" />
        </div>
      ) : polling ? (
        <div className="rounded-2xl border border-dashed border-primary/30 bg-primary/5 px-6 py-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <Loader2 className="w-7 h-7 text-primary/60 shrink-0 mt-0.5 sm:mt-0 animate-spin" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground mb-0.5">
              Analyse en cours…
            </p>
            <p className="text-xs text-muted-foreground">
              L'IA regroupe vos emails en projets. Cela peut prendre 1 à 2 minutes — cette page se met à jour automatiquement.
            </p>
          </div>
        </div>
      ) : suggestions.length === 0 && !analyzeMutation.isPending ? (
        <div className="rounded-2xl border border-dashed border-primary/30 bg-primary/5 px-6 py-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <Sparkles className="w-7 h-7 text-primary/60 shrink-0 mt-0.5 sm:mt-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground mb-0.5">
              Créez vos projets en un clic
            </p>
            <p className="text-xs text-muted-foreground">
              L'IA analyse vos emails et tâches pour proposer des regroupements en projets cohérents. Cliquez sur « Analyser maintenant » pour commencer.
            </p>
          </div>
          <Button
            size="sm"
            className="rounded-full shrink-0"
            onClick={() => { setHasInteracted(true); analyzeMutation.mutate(); }}
            disabled={analyzeMutation.isPending}
          >
            <Wand2 className="w-3.5 h-3.5 mr-1.5" />
            Analyser maintenant
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {suggestions.map((s) => (
            <SuggestionCard
              key={s.id}
              suggestion={s}
              onAccepted={() => {
                qc.invalidateQueries({ queryKey: PROJECT_SUGGESTIONS_QUERY_KEY });
                qc.invalidateQueries({ queryKey: getListProjectsQueryKey() });
                qc.invalidateQueries({
                  queryKey: getListTasksQueryKey(),
                  exact: false,
                });
                qc.invalidateQueries({ queryKey: getGetTodayQueryKey() });
              }}
              onDismissed={() =>
                qc.invalidateQueries({
                  queryKey: PROJECT_SUGGESTIONS_QUERY_KEY,
                })
              }
            />
          ))}
        </div>
      )}
    </section>
  );
}

function SuggestionCard({
  suggestion,
  onAccepted,
  onDismissed,
}: {
  suggestion: ProjectSuggestion;
  onAccepted: () => void;
  onDismissed: () => void;
}) {
  const { toast } = useToast();
  const [name, setName] = useState(suggestion.name);
  const [description, setDescription] = useState(suggestion.description ?? "");
  const [excludedIds, setExcludedIds] = useState<Set<string>>(new Set());
  // Expanded by default so the tasks contained in the suggested project are
  // immediately visible (the whole point: "des projets AVEC des tâches dedans").
  const [expanded, setExpanded] = useState(true);
  const [openTaskIds, setOpenTaskIds] = useState<Set<string>>(new Set());

  const acceptMutation = useMutation({
    mutationFn: () =>
      acceptProjectSuggestion(suggestion.id, {
        name: name.trim(),
        description: description.trim() || null,
        taskIds: suggestion.taskIds.filter((id) => !excludedIds.has(id)),
      }),
    onSuccess: (r) => {
      toast({
        title: "Projet créé",
        description: `${r.attachedTaskCount} ${r.attachedTaskCount === 1 ? "tâche rattachée" : "tâches rattachées"}.`,
      });
      onAccepted();
    },
    onError: (err: Error) =>
      toast({
        title: "Création impossible",
        description: err.message,
        variant: "destructive",
      }),
  });

  const dismissMutation = useMutation({
    mutationFn: () => dismissProjectSuggestion(suggestion.id),
    onSuccess: () => {
      toast({ title: "Suggestion ignorée" });
      onDismissed();
    },
    onError: (err: Error) =>
      toast({
        title: "Action impossible",
        description: err.message,
        variant: "destructive",
      }),
  });

  const selectedCount = suggestion.taskIds.length - excludedIds.size;

  const toggleTaskOpen = (id: string) =>
    setOpenTaskIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <Card
      className="rounded-2xl border-primary/20 bg-primary/5"
      data-testid={`card-suggested-project-${suggestion.id}`}
    >
      <CardContent className="p-4 sm:p-5 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0 space-y-2">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="font-semibold text-base bg-background/50 border-transparent hover:border-border focus-visible:border-ring h-9"
              placeholder="Nom du projet"
              data-testid="input-suggested-project-name"
            />
            {suggestion.reason && (
              <p className="text-xs text-muted-foreground italic">
                {suggestion.reason}
              </p>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0 -mr-1"
            onClick={() => setExpanded((v) => !v)}
            aria-label={expanded ? "Réduire" : "Développer"}
          >
            {expanded ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </Button>
        </div>

        {expanded && (
          <>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Description (optionnelle)"
              className="resize-none text-sm bg-background/50"
              data-testid="textarea-suggested-project-description"
            />

            <div className="space-y-1.5 bg-background/40 rounded-xl p-2">
              <div className="text-[11px] uppercase font-semibold text-muted-foreground tracking-wider px-1 pt-1">
                Tâches à rattacher ({selectedCount}/{suggestion.taskIds.length})
              </div>
              {suggestion.tasks.map((t) => (
                <SuggestionTaskRow
                  key={t.id}
                  task={t}
                  excluded={excludedIds.has(t.id)}
                  open={openTaskIds.has(t.id)}
                  onToggleOpen={() => toggleTaskOpen(t.id)}
                  onToggleExcluded={(checked) =>
                    setExcludedIds((prev) => {
                      const next = new Set(prev);
                      if (checked) next.delete(t.id);
                      else next.add(t.id);
                      return next;
                    })
                  }
                />
              ))}
            </div>
          </>
        )}

        <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => dismissMutation.mutate()}
            disabled={dismissMutation.isPending || acceptMutation.isPending}
            data-testid="button-dismiss-suggestion"
          >
            {dismissMutation.isPending ? (
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            ) : (
              <X className="w-3.5 h-3.5 mr-1.5" />
            )}
            Ignorer
          </Button>
          <Button
            size="sm"
            onClick={() => acceptMutation.mutate()}
            disabled={
              acceptMutation.isPending ||
              dismissMutation.isPending ||
              !name.trim() ||
              selectedCount < 1
            }
            data-testid="button-accept-suggestion"
          >
            {acceptMutation.isPending ? (
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            ) : (
              <Check className="w-3.5 h-3.5 mr-1.5" />
            )}
            Créer le projet
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function SuggestionTaskRow({
  task,
  excluded,
  open,
  onToggleOpen,
  onToggleExcluded,
}: {
  task: ProjectSuggestionTask;
  excluded: boolean;
  open: boolean;
  onToggleOpen: () => void;
  onToggleExcluded: (checked: boolean) => void;
}) {
  const [, navigate] = useLocation();
  const hasDescription = Boolean(task.description?.trim());
  return (
    <div
      className="rounded-lg hover:bg-secondary/40"
      data-testid={`row-suggested-task-${task.id}`}
    >
      <div className="flex items-center gap-2 px-2 py-1.5 text-sm">
        <Checkbox
          checked={!excluded}
          onCheckedChange={(checked) => onToggleExcluded(Boolean(checked))}
          aria-label="Inclure cette tâche dans le projet"
        />
        <button
          type="button"
          onClick={() =>
            navigate(`/app/today?taskId=${encodeURIComponent(task.id)}`)
          }
          className={
            "flex-1 min-w-0 text-left truncate hover:underline focus:outline-none focus:underline inline-flex items-center gap-1 " +
            (excluded ? "line-through text-muted-foreground" : "")
          }
          title="Voir la tâche"
          data-testid={`button-task-open-${task.id}`}
        >
          <span className="truncate">{task.title}</span>
          <ExternalLink className="w-3 h-3 shrink-0 opacity-60" />
        </button>
        {hasDescription && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleOpen();
            }}
            className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary/60 focus:outline-none focus:ring-1 focus:ring-ring"
            aria-label={open ? "Réduire la description" : "Voir la description"}
            aria-expanded={open}
            data-testid={`button-task-toggle-${task.id}`}
          >
            <ChevronDown
              className={
                "w-3.5 h-3.5 transition-transform " +
                (open ? "rotate-180" : "")
              }
            />
          </button>
        )}
      </div>
      {open && hasDescription && (
        <p className="text-xs text-muted-foreground px-9 pb-2 -mt-1 whitespace-pre-line">
          {task.description}
        </p>
      )}
    </div>
  );
}
